// Tako mini: the smallest box around the tako hub. The e-paper fills the tilted front (its
// board is as wide as the inside), slides in from below and is held by the base plate;
// the ESP32 lies low (wires soldered on top of its pins), the relay stands on edge at the
// right wall, the motor driver sticks under the ceiling. Two M3 screws at the front, two
// hooks at the back hold the base plate.
//
// Coordinates: mm, x = width, y = depth (front 0 → back), z = up; base plate below z = 0.

const rad = (d) => (d * Math.PI) / 180

export const MINI_PARAMS = [
  { group: 'Gehäuse', key: 'tilt', label: 'Neigung der Front', unit: '°', default: 15, min: 0, max: 30 },
  { group: 'Gehäuse', key: 'wall', label: 'Wandstärke', default: 1.6, min: 1.2, max: 3, step: 0.1 },
  { group: 'Gehäuse', key: 'radius', label: 'Eckenradius', default: 3, min: 0, max: 8, step: 0.5 },
  { group: 'Gehäuse', key: 'fit', label: 'Spiel', default: 0.3, min: 0.1, max: 0.8, step: 0.05 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdW', label: 'Platine Breite', default: 48, min: 30, max: 80, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdH', label: 'Platine Höhe', default: 33, min: 25, max: 60, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdStack', label: 'Glas + Platine dick', default: 2.6, min: 1.5, max: 5, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdBack', label: 'Platz dahinter (Stifte, Kabel)', default: 10, min: 3, max: 20 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdView', label: 'Sichtfenster', default: 28, min: 20, max: 40, step: 0.5 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdDx', label: 'Fenster seitlich versetzt', default: 0, min: -15, max: 15, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdDy', label: 'Fenster höher/tiefer', default: 0, min: -10, max: 10, step: 0.1 },
  { group: 'ESP32 DevKitC', key: 'espL', label: 'Länge', default: 54.4, min: 45, max: 70, step: 0.1 },
  { group: 'ESP32 DevKitC', key: 'espW', label: 'Breite', default: 28, min: 24, max: 32, step: 0.1 },
  { group: 'ESP32 DevKitC', key: 'espLift', label: 'Höhe über Boden (Stifte unten)', default: 12, min: 4, max: 20 },
  { group: 'Relais-Modul (hochkant)', key: 'relL', label: 'Länge', default: 50, min: 30, max: 80 },
  { group: 'Relais-Modul (hochkant)', key: 'relW', label: 'Breite (= Höhe hochkant)', default: 26, min: 15, max: 40 },
  { group: 'Relais-Modul (hochkant)', key: 'relT', label: 'Dicke mit Relais', default: 19, min: 10, max: 25 },
  { group: 'Vibrationsmotor-Treiber', key: 'vibL', label: 'Länge', default: 32, min: 15, max: 60 },
  { group: 'Vibrationsmotor-Treiber', key: 'vibW', label: 'Breite', default: 22, min: 10, max: 40 },
  { group: 'Vibrationsmotor-Treiber', key: 'vibT', label: 'Höhe', default: 10, min: 4, max: 20 },
]

export const miniDefaults = () => Object.fromEntries(MINI_PARAMS.map((p) => [p.key, p.default]))

const INSERT = { d: 4.0, depth: 6 }
const BASE_T = 2, RIM_H = 2.5, RIM_T = 1.4, PAD_R = 3.6

export function buildTakoMini(M, raw = {}) {
  const P = { ...miniDefaults(), ...raw }
  const { Manifold, CrossSection } = M
  const { wall, fit } = P
  const a = rad(P.tilt), ta = Math.tan(a), ca = Math.cos(a)
  const box = (x0, y0, z0, x1, y1, z1) => Manifold.cube([x1 - x0, y1 - y0, z1 - z0]).translate([x0, y0, z0])
  const cyl = (x, y, z0, h, d, seg = 40) => Manifold.cylinder(h, d / 2, d / 2, seg).translate([x, y, z0])
  const profile = (pts, x0, x1) => Manifold.extrude(CrossSection.ofPolygons([pts]), x1 - x0).rotate([90, 0, 90]).translate([x0, 0, 0])
  const foot = (x0, y0, x1, y1, rr, z0, z1) => {
    const cs = rr > 0.2 ? CrossSection.square([x1 - x0 - 2 * rr, y1 - y0 - 2 * rr]).translate([x0 + rr, y0 + rr]).offset(rr, 'Round', 2, 40) : CrossSection.square([x1 - x0, y1 - y0]).translate([x0, y0])
    return Manifold.extrude(cs, z1 - z0).translate([0, 0, z0])
  }

  // ── sizes from the parts ────────────────────────────────────────────────
  // Inside: as wide as the e-paper board (or ESP32 + relay on edge, whichever is more), as
  // high as the board standing on the base plate up the slope, deep enough for display +
  // its wires + the ESP32 lying behind them.
  const gap = 1.2
  const Win = Math.max(P.epdW + 2 * 0.4, P.espW + gap + P.relT + 0.6)
  // The tilted board stands on its back bottom corner; its top front corner sets the ceiling.
  const Hin = ((wall + P.epdStack) * ta + P.epdH) * ca - wall * Math.sin(a) + 0.4
  const W = Win + 2 * wall, H = Hin + wall
  const innerFrontY = (z) => z * ta + wall / ca
  // Behind the display (normal to the face): glass + board + room for pins and wires.
  const zone = (z) => innerFrontY(z) + (P.epdStack + P.epdBack) / ca
  // Back face of the display board, and the screw pads right behind it at the side walls
  // (the corners of the wire zone; a pin header sits in the middle of an edge).
  const boardBack = (z) => innerFrontY(z) + P.epdStack / ca
  const padH = 7, padY = boardBack(padH + PAD_R) + 0.4 + PAD_R
  const esp = { x0: wall + 0.4, lift: P.espLift }
  esp.x1 = esp.x0 + P.espW
  // Board behind the wire zone at its module's height; a low stop step 2 mm in front.
  esp.y0 = Math.max(zone(esp.lift + 1.6 + 3.3) + 0.5, zone(esp.lift + 1.6 + 0.6) + 2.3, padY + PAD_R + 4)
  esp.y1 = esp.y0 + P.espL
  const Din = esp.y1 + 3 // USB end 3 mm off the back wall
  const D = Din + wall
  const r = Math.min(P.radius, W / 2 - 1)
  const relay = { x1: W - wall - 0.3, y0: Math.max(zone(P.relW) + 0.5, padY + PAD_R + 1) }
  relay.x0 = relay.x1 - P.relT; relay.y1 = relay.y0 + P.relL

  // ── body ───────────────────────────────────────────────────────────────
  const outer = profile([[0, 0], [D, 0], [D, H], [H * ta, H]], 0, W).intersect(foot(0, 0, W, D, r, -1, H + 1))
  const zb = -1
  const cavity = profile([[innerFrontY(zb), zb], [Din, zb], [Din, Hin], [innerFrontY(Hin), Hin]], wall, W - wall)
    .intersect(foot(wall, -5, W - wall, Din, Math.max(0.3, r - wall), zb, Hin))
  let body = outer.subtract(cavity)

  // Front tilted frame: x across from the middle, y into the box from the outer face, z up
  // the slope from the bottom edge.
  const onFront = (m) => m.rotate([-P.tilt, 0, 0]).translate([W / 2, 0, 0])
  // Window over the e-paper's view area; the board's bottom edge rests on the base plate.
  const s0 = (wall + P.epdStack) * ta // the board's bottom edge on the base plate, up the slope
  const sPcb = s0 + P.epdH / 2
  const winX = P.epdDx, winZ = sPcb + P.epdDy, v = P.epdView, ch = Math.min(1.2, wall * 0.7)
  body = body.subtract(onFront(Manifold.union([
    box(winX - v / 2, -1, winZ - v / 2, winX + v / 2, wall + 1, winZ + v / 2),
    Manifold.hull([box(winX - v / 2 - ch - 1, -1.01, winZ - v / 2 - ch - 1, winX + v / 2 + ch + 1, -1, winZ + v / 2 + ch + 1), box(winX - v / 2, ch, winZ - v / 2, winX + v / 2, ch + 0.01, winZ + v / 2)]),
  ])))
  // Four small tabs on the side walls hold the board against the front (ends of its sides,
  // clear of a pin header in the middle of a short edge).
  const tabs = []
  const tabD = wall + P.epdStack + 0.15, tabL = 5
  for (const sx of [-1, 1]) for (const zc of [s0 + 3.5, s0 + P.epdH - 3.5]) {
    const x0 = sx < 0 ? -Win / 2 - 0.5 : Win / 2 - 1.6, x1 = sx < 0 ? -Win / 2 + 1.6 : Win / 2 + 0.5
    tabs.push(box(x0, tabD, zc - tabL / 2, x1, tabD + 1.4, zc + tabL / 2))
  }
  body = body.add(onFront(Manifold.union(tabs)).intersect(outer))

  // Front screw pads: short bosses at the floor against the side walls, behind the display
  // zone, a cone above so they print without support.
  const padAt = [[wall + PAD_R, padY], [W - wall - PAD_R, padY]]
  for (const [x, y] of padAt) body = body.add(cyl(x, y, 0, padH, 2 * PAD_R).add(Manifold.cylinder(PAD_R, PAD_R, 0.4, 40).translate([x, y, padH])).add(box(x - PAD_R, y - PAD_R, 0, x + PAD_R, y + PAD_R, padH).intersect(x < W / 2 ? box(0, -1, -1, x, D, padH + 1) : box(x, -1, -1, W, D, padH + 1))).intersect(outer))
  body = body.subtract(Manifold.union(padAt.map(([x, y]) => cyl(x, y, -1, INSERT.depth + 1, INSERT.d))))

  // Back wall: USB hole at the plug, a slot for the relay wires, two slots for the base
  // plate's hooks.
  const espCx = (esp.x0 + esp.x1) / 2
  const usbZ = esp.lift + 1.6 + 1.6
  const slotBox = (cx, z0, w, h) => box(cx - w / 2, Din - 1, z0, cx + w / 2, D + 1, z0 + h)
  const rounded = (cx, cz, w, h, rr) => Manifold.extrude(CrossSection.square([w - 2 * rr, h - 2 * rr]).translate([-w / 2 + rr, -h / 2 + rr]).offset(rr, 'Round', 2, 24), wall + 2).rotate([90, 0, 0]).translate([cx, D + 1, cz])
  const hookAt = [espCx, (relay.x0 + relay.x1) / 2]
  body = body.subtract(Manifold.union([
    rounded(espCx, usbZ, 12.5, 9, 3),
    slotBox((relay.x0 + relay.x1) / 2, 9, 10, 6),
    ...hookAt.map((x) => slotBox(x, 1.2, 8.6, 2.6)),
  ]))

  // ── base plate ─────────────────────────────────────────────────────────
  let base = foot(0, 0, W, D, r, -BASE_T, 0)
  // Rim keys into the sides and back (not the front: the display stands there).
  const ry0 = padY + PAD_R + 0.5
  let rim = foot(wall + fit, ry0, W - wall - fit, Din - fit, 0.5, 0, RIM_H).subtract(foot(wall + fit + RIM_T, ry0 - 1, W - wall - fit - RIM_T, Din - fit - RIM_T, 0.3, -1, RIM_H + 1))
  // Not under the pads, the ESP32's left pin row (the pins reach down to it) or the relay.
  rim = rim.subtract(Manifold.union(padAt.map(([x, y]) => cyl(x, y, -1, RIM_H + 2, 2 * PAD_R + 1)))).subtract(box(-1, esp.y0 + 1, -1, esp.x0 + 4, esp.y1 - 1, RIM_H + 1)).subtract(box(relay.x0 - 1, relay.y0 - 0.5, -1, W + 1, relay.y1 + 0.5, RIM_H + 1)) // the relay stands there
  base = base.add(rim)
  // ESP32 on two blocks between its header rows; front: stop lip + zip-tie slot; back block
  // carries the left hook. A second hook behind the relay.
  const bw = Math.max(8, P.espW - 9)
  const block = (y0, y1) => box(espCx - bw / 2, y0, 0, espCx + bw / 2, y1, esp.lift)
  let bridge = Manifold.union([block(esp.y0 - 0.2, esp.y0 + 5), block(esp.y1 - 4, esp.y1)])
  bridge = bridge.add(box(espCx - bw / 2, esp.y0 - 2.2, 0, espCx + bw / 2, esp.y0 - 0.2, esp.lift + 1.6 + 0.6)) // low stop step
  if (esp.lift >= 8) bridge = bridge.subtract(box(espCx - bw / 2 - 1, esp.y0 + 1, esp.lift - 5.5, espCx + bw / 2 + 1, esp.y0 + 4, esp.lift - 3.5)) // zip-tie slot
  // Hook: a block grown into the rim (no face shared with it) and a nub out through the back.
  const hook = (cx) => box(cx - 4, Din - fit - RIM_T - 0.4, 0, cx + 4, Din - fit - 0.3, 5).add(box(cx - 3.8, Din - fit - 0.6, 1.5, cx + 3.8, Din + wall * 0.8, 3.3))
  base = base.add(bridge).add(Manifold.union(hookAt.map(hook)))
  // Countersunk M3 at the front pads.
  base = base.subtract(Manifold.union(padAt.map(([x, y]) => Manifold.union([cyl(x, y, -BASE_T - 1, BASE_T + 2, 3.4), Manifold.cylinder(1.6, 3.2, 1.6, 40).translate([x, y, -BASE_T - 0.01])]))))

  const toBed = (m) => { const b = m.boundingBox(); return m.translate([-b.min[0], -b.min[1], -b.min[2]]) }
  const vib = { x0: espCx - P.vibW / 2, y0: Math.max(zone(Hin - P.vibT), esp.y0 + 2) }
  vib.x1 = vib.x0 + P.vibW; vib.y1 = vib.y0 + P.vibL
  const ghosts = [
    { name: 'ESP32 DevKitC', solid: box(esp.x0, esp.y0, esp.lift, esp.x1, esp.y1, esp.lift + 1.6).add(box(espCx - 9, esp.y0, esp.lift + 1.6, espCx + 9, esp.y0 + 25.5, esp.lift + 4.9)).add(box(esp.x0 + 0.6, esp.y0 + 3, esp.lift - 11, esp.x0 + 3.2, esp.y1 - 3, esp.lift)).add(box(esp.x1 - 3.2, esp.y0 + 3, esp.lift - 11, esp.x1 - 0.6, esp.y1 - 3, esp.lift)) },
    { name: 'Relais', solid: box(relay.x0, relay.y0, 0.2, relay.x1, relay.y1, P.relW) },
    { name: 'Vibrationstreiber', solid: box(vib.x0, vib.y0, Hin - P.vibT, vib.x1, vib.y1, Hin - 0.1) },
    { name: 'E-Paper', solid: onFront(box(-P.epdW / 2, wall + 0.05, s0 + 0.05, P.epdW / 2, wall + P.epdStack, s0 + P.epdH)).subtract(box(-1e3, -1e3, -50, 1e3, 1e3, 0)) },
  ]
  const issues = []
  if (relay.x0 < esp.x1 + 0.8) issues.push('ESP32 und Relais passen nicht nebeneinander — Relais dünner oder anders einbauen.')
  if (relay.y1 > Din - 1) issues.push('Relais ist zu lang für die Tiefe.')
  if (vib.y1 > Din - 1 || Hin - P.vibT < esp.lift + 1.6 + 3.3 + 3) issues.push('Vibrationstreiber passt nicht unter die Decke über dem ESP32.')
  if (P.relW > Hin - 0.5) issues.push('Relais ist hochkant zu hoch für das Gehäuse.')
  const parts = [
    { id: 'gehaeuse', name: 'Gehäuse', solid: body, color: '#d8d4cc', print: toBed(body.rotate([180, 0, 0])), explode: [0, 0, 30], note: 'kopfüber drucken, ohne Stützen' },
    { id: 'boden', name: 'Bodenplatte', solid: base, color: '#8fa38a', print: toBed(base), explode: [0, 0, -18], note: 'flach, Unterseite aufs Bett' },
  ]
  return { parts, ghosts, issues, params: P, size: [W, D, H + BASE_T] }
}
